import {mount} from 'svelte';

import {consumeFeatureUse, releaseFeatureUse} from '@js/modules/featureLimit';
import {downloadExport} from '@js/actions/exporterActions';

import I18n from '@js/modules/translations';
import {onPageReady} from '@js/modules/utils';
import AnalyticsService from '@js/modules/analyticsService';
import StorageService from '@js/modules/storage';
import {mapStore} from '@js/stores/mapStore';

import Notification from '@js/components/layouts/notification';

const BiggerPicture = () => import('bigger-picture');

const RideShow = () => import('@js/components/ride/RideShow.svelte');

const RideElevationGraph = () => import('@js/components/ride/shared/RideElevationGraph.svelte');

const ShareRideButton = () => import('@js/components/ride/share/ShareRideButton.svelte');

const FavoriteButton = () => import('@js/components/ride/FavoriteButton.svelte');

const RideRouteInfo = () => import('@js/components/ride/RideRouteInfo.svelte');
const HeartRateGraph = () => import('@js/components/maps/HeartRateGraph.svelte');
const RideComments = () => import('@js/components/ride/comments/RideComments.svelte');
const RideFlyover = () => import('@js/components/ride/RideFlyover.svelte');
const RideWaypointsTimeline = () => import('@js/components/ride/RideWaypointsTimeline.svelte');

// Loaded at the first click of their button only
const RideActionModals = {
    'live-preview': () => import('@js/components/ride/RideLiveTrackingModal.svelte'),
    complete: () => import('@js/components/ride/RideCompleteModal.svelte')
};


class RideManager {
    _ride = null;

    _biggerPicture = null;

    _$rideData = document.getElementById('ride-data');

    _$map = document.getElementById('ride-map-component');
    _$mapElevation = document.getElementById('ride-map-elevation-component');
    _$shareButton = document.getElementById('share-component');
    _$favoriteButton = document.getElementById('favorite-component');
    _$routeInfo = document.getElementById('ride-route-info-component');
    _$heartRate = document.getElementById('ride-heart-rate-component');
    _$flyover = document.getElementById('ride-flyover-component');
    _$waypoints = document.getElementById('ride-waypoints-component');

    _$images = document.querySelectorAll('#ride-carrousel a');

    _$comments = document.getElementById('ride-comment-component');


    /* Public methods
    ******************** */
    constructor() {
        this._ride = JSON.parse(this._$rideData.innerText)?.ride;
    }

    initialize = () => {
        this._saveVisitedRide();

        this._mountComponents();

        this._manageImages();

        this._onExportClick();

        this._onOpenMapClick();

        this._onLocatePictureClick();

        this._onRideActionClick();
    };

    /* Private methods
    ******************** */
    _saveVisitedRide = () => {
        StorageService.saveLocalArray('rides-visited', [this._ride.id], true, true);
    };

    _mountComponents = () => {
        RideShow()
            .then(({default: RideShowComponent}) => {
                mount(RideShowComponent, {
                    target: this._$map,
                    props: {
                        ride: this._ride,
                        hasHeartRate: this._$map.dataset.heartRate === 'true'
                    }
                });
            });

        if (this._$mapElevation) {
            onPageReady(() => {
                RideElevationGraph()
                    .then(({default: RideElevationGraphComponent}) => {
                        mount(RideElevationGraphComponent, {
                            target: this._$mapElevation,
                            props: {
                                rideId: this._ride.id,
                                rideShareUrl: this._ride.shareUrl
                            }
                        });
                    });
            });
        }

        if (this._$shareButton) {
            onPageReady(() => {
                ShareRideButton()
                    .then(({default: ShareRideButtonComponent}) => {
                        mount(ShareRideButtonComponent, {
                            target: this._$shareButton,
                            props: {
                                rideId: this._ride.id,
                                rideName: this._ride.name,
                                rideUrl: window.location.origin + this._ride.link,
                                rideVisibility: this._ride.visibility,
                                rideSharedUrl: this._ride.sharedUrl
                            }
                        });
                    });
            });
        }

        if (this._$favoriteButton) {
            onPageReady(() => {
                FavoriteButton()
                    .then(({default: FavoriteButtonComponent}) => {
                        mount(FavoriteButtonComponent, {
                            target: this._$favoriteButton,
                            props: {
                                rideId: this._ride.id,
                                variant: 'action'
                            }
                        });
                    });
            });
        }

        if (this._$routeInfo) {
            onPageReady(() => {
                RideRouteInfo()
                    .then(({default: RideRouteInfoComponent}) => {
                        mount(RideRouteInfoComponent, {
                            target: this._$routeInfo,
                            props: {
                                latitude: this._ride.coordinates?.[0],
                                longitude: this._ride.coordinates?.[1],
                                rideId: this._ride.id,
                                rideName: this._ride.name
                            }
                        });
                    });
            });
        }

        if (this._$heartRate) {
            onPageReady(() => {
                HeartRateGraph()
                    .then(({default: HeartRateGraphComponent}) => {
                        mount(HeartRateGraphComponent, {
                            target: this._$heartRate,
                            props: {ride: this._ride}
                        });
                    });
            });
        }

        if (this._$waypoints) {
            onPageReady(() => {
                RideWaypointsTimeline()
                    .then(({default: RideWaypointsTimelineComponent}) => {
                        mount(RideWaypointsTimelineComponent, {
                            target: this._$waypoints,
                            props: {
                                rideId: this._ride.id,
                                rideSpeed: this._ride.speed,
                                hasElevation: !!this._ride.positiveElevation && this._$waypoints.dataset.elevation === 'true',
                                recorded: this._$waypoints.dataset.recorded === 'true',
                                timeZone: this._$waypoints.dataset.timeZone || null
                            }
                        });
                    });
            });
        }

        if (this._$flyover) {
            onPageReady(() => {
                RideFlyover()
                    .then(({default: RideFlyoverComponent}) => {
                        mount(RideFlyoverComponent, {
                            target: this._$flyover,
                            props: {
                                rideUrl: window.location.origin + this._ride.link,
                                rideName: this._ride.name
                            }
                        });
                    });
            });
        }

        if (this._$comments && this._ride.allowComment !== false) {
            onPageReady(() => {
                RideComments()
                    .then(({default: RideCommentsComponent}) => {
                        mount(RideCommentsComponent, {
                            target: this._$comments,
                            props: {
                                commentableType: 'rides',
                                commentableId: this._ride.id,
                                ownerId: this._ride.userId,
                                allowComment: this._ride.allowComment !== false
                            }
                        });
                    });
            });
        }
    };

    _openImage = (event) => {
        event.preventDefault();

        this._biggerPicture.open({
            items: this._$images,
            el: event.currentTarget,
            scale: 0.85,
            maxZoom: 3
        });
    };

    _manageImages = () => {
        onPageReady(() => {
            BiggerPicture()
                .then(({default: BiggerPictureComponent}) => {
                    import('bigger-picture/css');

                    this._biggerPicture = BiggerPictureComponent({
                        target: document.body
                    });

                    this._$images.forEach(($image) => $image.addEventListener('click', this._openImage));
                });
        });
    };

    _cappedExportFormats = ['gpx', 'kml', 'tcx'];

    _onExportClick = () => {
        this._cappedExportFormats.forEach((format) => {
            const $button = document.getElementById(`export-${format}`);
            if (!$button) {
                return;
            }

            $button.onclick = (event) => {
                // Fetched, not left to the link: a refused export (500, lost connection) must not cost one.
                event.preventDefault();

                // Same quota as the planner export: nil = unlimited, 0 = none; opens the paywall when spent.
                if (!consumeFeatureUse('ride_export_gpx')) {
                    return;
                }

                Notification.info(I18n.t('js.ride.show.exports.progress'));

                // Counted once the file came; a refused export gives its use back
                downloadExport($button.getAttribute('href'), format)
                    .then((downloaded) => {
                        if (downloaded) {
                            AnalyticsService.trackRouteExported(this._ride.id, this._ride.name);
                        } else {
                            releaseFeatureUse('ride_export_gpx');
                        }
                    });
            };
        });

        const $pdfButton = document.getElementById('export-pdf');
        if ($pdfButton) {
            $pdfButton.onclick = () => {
                AnalyticsService.trackRouteExported(this._ride.id, this._ride.name);
            };
        }
    };

    // Carousel photo of a POI: its pin button scrolls up to the map and centres it on the photo.
    _onLocatePictureClick = () => {
        document.querySelectorAll('[data-locate-poi]').forEach(($button) => {
            $button.addEventListener('click', (event) => {
                event.preventDefault();

                this._$map?.scrollIntoView({behavior: 'smooth', block: 'start'});
                // Close enough to tell the photo apart inside its fanned bunch.
                mapStore.map?.focusPoiById?.(Number($button.dataset.locatePoi), {zoom: 16});
            });
        });
    };

    _actionModals = {};

    _actionModalProps = (action, $button) => {
        if (action === 'complete') {
            return {
                rideId: this._ride.id,
                defaultDate: $button.dataset.defaultDate,
                durationComputed: this._ride.durationComputed,
                speedComputed: this._ride.speedComputed
            };
        }

        return {testerUrl: $button.dataset.testerUrl};
    };

    // Buttons of the page (live tracking preview, mark as completed): one modal per action, mounted once
    _onRideActionClick = () => {
        document.querySelectorAll('[data-ride-action]').forEach(($button) => {
            $button.addEventListener('click', (event) => {
                event.preventDefault();

                const action = $button.dataset.rideAction;
                if (!RideActionModals[action]) {
                    return;
                }

                // The pending mount is kept: a second tap while the chunk loads opens the same modal, never a second one
                if (this._actionModals[action]) {
                    this._actionModals[action].then((modal) => modal?.open());

                    return;
                }

                this._actionModals[action] = RideActionModals[action]()
                    .then(({default: ModalComponent}) => mount(ModalComponent, {
                        target: document.body,
                        props: {...this._actionModalProps(action, $button), isModalOpen: true}
                    }))
                    .catch((error) => {
                        // A stale chunk after a deploy: the next tap tries again
                        this._actionModals[action] = null;
                        console.error(error);
                    });
            });
        });
    };

    _onOpenMapClick = () => {
        const $openMap = document.getElementById('open-map');
        if (!$openMap) {
            return;
        }

        $openMap.addEventListener('click', (event) => {
            event.preventDefault();

            const lat = $openMap.dataset.lat;
            const lng = $openMap.dataset.lng;
            const label = encodeURIComponent($openMap.dataset.label);
            const userAgent = navigator.userAgent || navigator.vendor;

            if (/iPad|iPhone|iPod/.test(userAgent)) {
                window.location.href = `https://maps.apple.com/?ll=${lat},${lng}&q=${label}`;
            } else if (/android/i.test(userAgent)) {
                window.location.href = `geo:${lat},${lng}?q=${label}`;
            } else {
                window.location.href = `https://www.google.com/maps?q=${lat},${lng}(${label})`;
            }
        });
    };
}

export default new RideManager();
